/**
 * Prévia diagramada no padrão do Diário Oficial — papel, colunas e medidas.
 *
 * A prévia usa unidades físicas (cm/mm do CSS) com a MESMA métrica da
 * estimativa de composição: `LINHAS_POR_CM` (2,2 linhas/cm) vira uma
 * line-height de 1/2,2 cm. Assim a altura que o cliente vê na régua é a
 * altura que a estimativa cobra — prévia e conta não podem divergir, senão
 * o valor "estimado" vira surpresa na medição real.
 *
 * O fluxo em N colunas usa CSS multi-column: é exatamente o problema da
 * diagramação (fluir o mesmo texto em colunas de altura balanceada), e o
 * navegador resolve igual ao compositor.
 */

import { useEffect, useRef, useState } from 'react'

import { urlMidia } from '../../api/cliente'

import {
  formatarCentavos,
  LINHAS_POR_CM,
  ROTULOS_DEMONSTRACAO,
  type Demonstracao,
  type LinhaConta,
} from '@egba-do/shared'

/** Largura útil de UMA coluna do Diário, em cm (padrão de composição EGBA). */
const LARGURA_COLUNA_CM = 8.6
/** Vão entre colunas, em mm. */
const VAO_MM = 4

export interface CabecalhoPrevia {
  razaoSocial: string
  cnpj: string | null
  subtitulo?: string
  /** Logomarca da organização — topo e rodapé da peça. */
  logoUrl?: string | null
}

export interface CapaPrevia {
  imagemUrl?: string | null
  titulo?: string
  subtitulo?: string
  /** Gráfico de destaque (receita × lucro) desenhado dos próprios números. */
  grafico?: boolean
}

/**
 * Gráfico de destaque — SVG determinístico desenhado das linhas da DRE
 * (receita líquida/bruta e lucro líquido, exercício × comparativo). Nenhum
 * número novo: se a conta não está na DRE, a barra não existe.
 */
function GraficoDestaque({ demonstracoes }: { demonstracoes: Demonstracao[] }) {
  const dre = demonstracoes.find((d) => d.tipo === 'dre')
  if (!dre) return null
  const acha = (t: string) =>
    dre.linhas.find((l) => l.descricao.toLowerCase().includes(t) && l.valorCentavos !== null)
  const receita = acha('receita operacional líquida') ?? acha('receita líquida') ?? acha('receita')
  const lucro = acha('lucro líquido')
  if (!receita || !lucro) return null

  const series = [
    { rotulo: `Receita`, atual: receita.valorCentavos ?? 0, anterior: receita.comparativoCentavos ?? null },
    { rotulo: `Lucro líquido`, atual: lucro.valorCentavos ?? 0, anterior: lucro.comparativoCentavos ?? null },
  ]
  const maior = Math.max(1, ...series.flatMap((s) => [Math.abs(s.atual), Math.abs(s.anterior ?? 0)]))
  const mi = (v: number) => `R$ ${(v / 1e8).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`
  const temAnterior = series.some((s) => s.anterior !== null)

  return (
    <section className="break-inside-avoid-column" style={{ marginBottom: `${(2 / LINHAS_POR_CM).toFixed(3)}cm` }}>
      <h3 className="text-center font-bold uppercase">Destaques do exercício</h3>
      <svg viewBox="0 0 240 96" className="mx-auto mt-[1mm] block w-full" role="img" aria-label="Gráfico de receita e lucro">
        {series.map((s, i) => {
          const y = 8 + i * 44
          const wAtual = Math.max(2, (Math.abs(s.atual) / maior) * 150)
          const wAnt = s.anterior === null ? 0 : Math.max(2, (Math.abs(s.anterior) / maior) * 150)
          return (
            <g key={s.rotulo}>
              <text x="0" y={y + 10} fontSize="7" fill="#111" fontFamily="Georgia, serif">{s.rotulo}</text>
              <rect x="58" y={y} width={wAtual} height="10" fill="#1e293b" />
              <text x={62 + wAtual} y={y + 8} fontSize="6.5" fill="#111" fontFamily="Georgia, serif">
                {dre.exercicio} · {mi(s.atual)}
              </text>
              {temAnterior && s.anterior !== null && (
                <>
                  <rect x="58" y={y + 13} width={wAnt} height="10" fill="#94a3b8" />
                  <text x={62 + wAnt} y={y + 21} fontSize="6.5" fill="#334155" fontFamily="Georgia, serif">
                    {dre.exercicioComparativo} · {mi(s.anterior)}
                  </text>
                </>
              )}
            </g>
          )
        })}
      </svg>
    </section>
  )
}

/**
 * Prova em papel: abre uma janela com o mesmo papel da prévia e imprime.
 *
 * O stylesheet do app é referenciado por URL (mesma origem), então as classes
 * do papel renderizam idênticas — mesma fidelidade da tela. A tarja "PROVA DE
 * COMPOSIÇÃO" deixa claro que a folha não é a publicação oficial.
 */
function imprimirProva(papel: HTMLElement | null): void {
  if (!papel) return
  const estilos = [...document.querySelectorAll('link[rel="stylesheet"]')]
    .map((l) => `<link rel="stylesheet" href="${(l as HTMLLinkElement).href}">`)
    .join('')
  const janela = window.open('', '_blank', 'width=900,height=700')
  if (!janela) return
  janela.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>Prova de composição — EGBA</title>${estilos}
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      body { margin: 0; display: flex; flex-direction: column; align-items: center; background: #fff; }
      .tarja { font: 600 10px system-ui, sans-serif; color: #b45309; border: 1px solid #f59e0b;
               border-radius: 6px; padding: 4px 10px; margin: 8mm 0 4mm; }
    </style></head><body>
    <p class="tarja">PROVA DE COMPOSIÇÃO — sem valor oficial. A medição e a publicação são feitas pela EGBA.</p>
    ${papel.outerHTML}
    </body></html>`)
  janela.document.close()
  janela.addEventListener('load', () => {
    janela.focus()
    janela.print()
  })
}

function formatarCnpj(v: string | null): string {
  if (!v) return ''
  const d = v.replace(/\D/g, '')
  return d.length === 14
    ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
    : v
}

function Valor({ centavos }: { centavos: number | null | undefined }) {
  if (centavos === null || centavos === undefined) return null
  const negativo = centavos < 0
  const texto = formatarCentavos(Math.abs(centavos))
  return <span>{negativo ? `(${texto})` : texto}</span>
}

function LinhaTabela({ linha, comparativo }: { linha: LinhaConta; comparativo: boolean }) {
  return (
    <div
      className={`flex items-baseline gap-[2mm] ${linha.grupo ? 'border-t border-black font-bold' : ''}`}
      style={{ paddingLeft: `${linha.nivel * 3}mm` }}
    >
      <span className="min-w-0 flex-1 truncate">{linha.descricao || '—'}</span>
      <span className="shrink-0 text-right tabular-nums">
        <Valor centavos={linha.valorCentavos} />
      </span>
      {comparativo && (
        <span className="w-[16mm] shrink-0 text-right tabular-nums">
          <Valor centavos={linha.comparativoCentavos} />
        </span>
      )}
    </div>
  )
}

function BlocoPrevia({ d }: { d: Demonstracao }) {
  const rotulo = ROTULOS_DEMONSTRACAO[d.tipo]
  const comparativo = !!d.exercicioComparativo
  const ativo = d.tipo === 'balanco_patrimonial' ? d.linhas.filter((l) => l.lado !== 'passivo') : d.linhas
  const passivo = d.tipo === 'balanco_patrimonial' ? d.linhas.filter((l) => l.lado === 'passivo') : []

  return (
    <section className="break-inside-avoid-column" style={{ marginBottom: `${(2 / LINHAS_POR_CM).toFixed(3)}cm` }}>
      <h3 className="text-center font-bold uppercase">{rotulo.nome}</h3>
      <p className="text-center">
        {comparativo
          ? `Exercícios findos em 31 de dezembro de ${d.exercicio} e ${d.exercicioComparativo} (em reais)`
          : `Exercício findo em 31 de dezembro de ${d.exercicio} (em reais)`}
      </p>
      {d.texto && <p className="mt-[1mm] text-justify whitespace-pre-wrap">{d.texto}</p>}
      {(comparativo && d.linhas.some((l) => l.valorCentavos !== null)) && (
        <div className="flex justify-end gap-[2mm] font-bold">
          <span className="text-right">{d.exercicio}</span>
          <span className="w-[16mm] text-right">{d.exercicioComparativo}</span>
        </div>
      )}
      {passivo.length === 0 ? (
        ativo.map((l, i) => <LinhaTabela key={i} linha={l} comparativo={comparativo} />)
      ) : (
        <>
          <p className="font-bold uppercase">Ativo</p>
          {ativo.map((l, i) => (
            <LinhaTabela key={`a${i}`} linha={l} comparativo={comparativo} />
          ))}
          <p className="mt-[1mm] font-bold uppercase">Passivo e Patrimônio Líquido</p>
          {passivo.map((l, i) => (
            <LinhaTabela key={`p${i}`} linha={l} comparativo={comparativo} />
          ))}
        </>
      )}
    </section>
  )
}

/**
 * A régua lateral marca centímetros reais — os mesmos que a tabela de preços
 * cobra. Medida no DOM, não na estimativa: quando as duas divergem muito, é a
 * estimativa que precisa de ajuste, e ver isso aqui é o jeito de descobrir.
 */
export function PreviaDiario({
  cabecalho,
  demonstracoes,
  colunas,
  capa,
}: {
  cabecalho: CabecalhoPrevia
  demonstracoes: Demonstracao[]
  colunas: number
  capa?: CapaPrevia | null
}) {
  const papel = useRef<HTMLDivElement>(null)
  const [alturaCm, setAlturaCm] = useState<number | null>(null)

  useEffect(() => {
    if (!papel.current) return
    const el = papel.current
    const medir = () => {
      // 1cm CSS = 96px/2.54 — a régua e a altura medida usam a mesma conversão.
      setAlturaCm(Math.round((el.scrollHeight / (96 / 2.54)) * 10) / 10)
    }
    medir()
    const obs = new ResizeObserver(medir)
    obs.observe(el)
    return () => obs.disconnect()
  }, [demonstracoes, colunas, cabecalho])

  const largura = colunas * LARGURA_COLUNA_CM + ((colunas - 1) * VAO_MM) / 10

  return (
    <div className="overflow-x-auto rounded-xl border border-[color:var(--color-borda)] bg-slate-100 p-4">
      <div className="mx-auto flex w-fit items-stretch gap-2">
        {/* régua em cm */}
        <div
          className="relative w-5 shrink-0 rounded-sm bg-white text-[8px] text-slate-400"
          style={{
            backgroundImage:
              'repeating-linear-gradient(to bottom, transparent, transparent calc(1cm - 1px), #cbd5e1 calc(1cm - 1px), #cbd5e1 1cm)',
          }}
          aria-hidden
        >
          {alturaCm !== null &&
            Array.from({ length: Math.floor(alturaCm) }, (_, i) => (
              <span key={i} className="absolute right-0.5" style={{ top: `calc(${i + 1}cm - 9px)` }}>
                {i + 1}
              </span>
            ))}
        </div>

        {/* papel */}
        <div
          ref={papel}
          className="shrink-0 bg-white px-[4mm] py-[3mm] shadow-md"
          style={{
            width: `${largura + 0.8}cm`,
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: '2.6mm',
            lineHeight: `${(1 / LINHAS_POR_CM).toFixed(4)}cm`,
            color: '#111',
          }}
        >
          {/* ------------------------------------------------- capa diagramada */}
          {capa && (capa.imagemUrl || capa.titulo) && (
            <div className="text-center" style={{ marginBottom: `${(3 / LINHAS_POR_CM).toFixed(3)}cm` }}>
              {capa.imagemUrl && (
                <img
                  src={urlMidia(capa.imagemUrl) ?? undefined}
                  alt="Capa da publicação"
                  className="mx-auto block w-full object-cover"
                  style={{ maxHeight: '52mm' }}
                />
              )}
              <p className="mt-[2mm] font-bold uppercase" style={{ fontSize: '4.6mm', lineHeight: 1.15 }}>
                {capa.titulo || 'Demonstrações Contábeis'}
              </p>
              {capa.subtitulo && (
                <p className="italic" style={{ fontSize: '3mm' }}>
                  {capa.subtitulo}
                </p>
              )}
              <div className="mx-auto mt-[2mm] border-b-2 border-black" />
            </div>
          )}
          <header
            className="text-center"
            style={{ marginBottom: `${(2 / LINHAS_POR_CM).toFixed(3)}cm` }}
          >
            {cabecalho.logoUrl && (
              <img
                src={urlMidia(cabecalho.logoUrl) ?? undefined}
                alt=""
                className="mx-auto mb-[1mm] block object-contain"
                style={{ maxHeight: '12mm', maxWidth: '45mm' }}
              />
            )}
            <p className="font-bold uppercase" style={{ fontSize: '3.2mm' }}>
              {cabecalho.razaoSocial || 'RAZÃO SOCIAL DA COMPANHIA'}
            </p>
            {cabecalho.cnpj && <p>CNPJ nº {formatarCnpj(cabecalho.cnpj)}</p>}
            {cabecalho.subtitulo && <p className="italic">{cabecalho.subtitulo}</p>}
            <div className="mx-auto mt-[1mm] border-b border-black" />
          </header>
          <div style={{ columnCount: colunas, columnGap: `${VAO_MM}mm`, columnRule: '1px solid #ddd' }}>
            {capa?.grafico && <GraficoDestaque demonstracoes={demonstracoes} />}
            {demonstracoes.map((d, i) => (
              <BlocoPrevia key={i} d={d} />
            ))}
          </div>
          {/* rodapé da peça */}
          <footer
            className="flex items-center justify-center gap-[3mm] border-t border-black text-center"
            style={{ marginTop: `${(2 / LINHAS_POR_CM).toFixed(3)}cm`, paddingTop: '1.5mm', fontSize: '2.2mm', color: '#333' }}
          >
            {cabecalho.logoUrl && (
              <img src={urlMidia(cabecalho.logoUrl) ?? undefined} alt="" className="block object-contain" style={{ maxHeight: '6mm', maxWidth: '20mm' }} />
            )}
            <span>
              {cabecalho.razaoSocial}
              {cabecalho.cnpj ? ` · CNPJ ${formatarCnpj(cabecalho.cnpj)}` : ''}
            </span>
          </footer>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        <p className="text-center text-[11px] text-slate-500">
          Prévia de composição no padrão do Diário — altura medida:{' '}
          <strong>{alturaCm === null ? '…' : `${alturaCm.toLocaleString('pt-BR')} cm`}</strong> em{' '}
          {colunas} coluna{colunas > 1 ? 's' : ''} de {LARGURA_COLUNA_CM.toLocaleString('pt-BR')} cm.
          A medição oficial acontece na composição final — esta prévia é estimativa.
        </p>
        <button
          type="button"
          onClick={() => imprimirProva(papel.current)}
          className="rounded-lg border border-[color:var(--color-borda)] bg-white px-3 py-1 text-[11px] font-medium text-slate-700 transition hover:bg-slate-50"
        >
          🖨 Imprimir prova
        </button>
      </div>
    </div>
  )
}
